<template>
  <div class="ai-text-actions">
    <v-menu
      v-model="menuOpen"
      :close-on-content-click="false"
      offset-y
      nudge-bottom="4"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          small
          outlined
          color="primary"
          class="ai-btn"
          :loading="loading"
          v-bind="attrs"
          v-on="on"
        >
          <v-icon
            small
            left
          >
            {{ mdiCreation }}
          </v-icon>{{ $t("\n          AI Text · 1 credit\n        ") }}
        </v-btn>
      </template>

      <v-card
        class="ai-menu-card"
        min-width="220"
      >
        <v-list dense>
          <!-- Grammar (expandable) -->
          <v-list-group no-action>
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title>{{ $t("Grammar") }}</v-list-item-title>
              </v-list-item-content>
            </template>
            <v-list-item @click="runAction('grammar-formal')">
              <v-list-item-title class="pl-4">
                {{ $t("\n                Formal\n              ") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="runAction('grammar-casual')">
              <v-list-item-title class="pl-4">
                {{ $t("\n                Personal\n              ") }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Creative (expandable) -->
          <v-list-group no-action>
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title>{{ $t("Creative") }}</v-list-item-title>
              </v-list-item-content>
            </template>
            <v-list-item @click="runAction('creative-dramatic')">
              <v-list-item-title class="pl-4">
                {{ $t("\n                Dramatic\n              ") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="runAction('creative-poetic')">
              <v-list-item-title class="pl-4">
                {{ $t("\n                Poetic\n              ") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item @click="runAction('creative-cinematographic')">
              <v-list-item-title class="pl-4">
                {{ $t("\n                Cinematographic\n              ") }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>

          <!-- Summarize (single action) -->
          <v-list-item @click="runAction('summarize')">
            <v-list-item-title>{{ $t("Summarize") }}</v-list-item-title>
          </v-list-item>

          <!-- Custom -->
          <v-list-item @click="showCustomDialog = true; menuOpen = false">
            <v-list-item-title>{{ $t("Custom") }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-card>
    </v-menu>

    <v-btn
      v-if="hasOriginal"
      small
      outlined
      color="warning"
      class="ai-btn"
      @click="restoreOriginal"
    >
      <v-icon
        small
        left
      >
        {{ mdiUndo }}
      </v-icon>{{ $t("\n      Restore\n    ") }}
    </v-btn>

    <!-- Custom prompt dialog -->
    <v-dialog
      v-model="showCustomDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>{{ $t("Custom AI Action") }}</v-card-title>
        <v-card-text>
          <v-textarea
            v-model="customPrompt"
            :label="$t(&quot;Describe how you want the AI to transform your text&quot;)"
            :placeholder="$t(&quot;e.g., Make it sound more enthusiastic, translate to Spanish, add metaphors...&quot;)"
            outlined
            rows="3"
            hide-details
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="showCustomDialog = false"
          >
            {{ $t("\n            Cancel\n          ") }}
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!customPrompt.trim()"
            @click="runAction('custom')"
          >
            {{ $t("\n            Apply\n          ") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-alert
      v-if="aiMessage"
      dense
      outlined
      :type="aiFailed ? 'error' : 'success'"
      role="status"
      class="mt-2"
    >
      {{ aiMessage }}
    </v-alert>
  </div>
</template>

<script>
import { mdiCreation, mdiUndo } from '@mdi/js'
import { t } from '@/i18n'

import { runAi, aiError } from '@/services/ai'

export default {
  name: 'AiTextActions',
  props: {
    value: {
      type: String,
      default: '',
    },
    originalText: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      mdiCreation,
      mdiUndo,
      menuOpen: false,
      loading: false,
      aiMessage: '',
      aiFailed: false,
      showCustomDialog: false,
      customPrompt: '',
      endpoint: process.env.VUE_APP_ROOT_API,
    }
  },
  computed: {
    hasOriginal() {
      return !!(this.originalText && this.originalText.trim())
    },
  },
  methods: {
    stripHtml(html) {
      const tmp = document.createElement('div')
      tmp.innerHTML = html

      return tmp.textContent || tmp.innerText || ''
    },

    // Extracts inline media (img, video, audio, iframe, picture, figure)
    // from the HTML and replaces each with a [[MEDIA_N]] placeholder token.
    // Returns { text, mediaMap } where mediaMap[N] is the original outerHTML.
    extractMedia(html) {
      const tmp = document.createElement('div')
      tmp.innerHTML = html || ''

      const selector = 'img, video, audio, iframe, picture, figure'
      const nodes = Array.from(tmp.querySelectorAll(selector))
      const mediaMap = []

      nodes.forEach(node => {
        // Skip nodes already consumed by a captured ancestor (e.g. <img> inside <figure>).
        if (!node.parentNode) return
        mediaMap.push(node.outerHTML)
        const placeholder = document.createTextNode(`\n[[MEDIA_${mediaMap.length - 1}]]\n`)
        node.parentNode.replaceChild(placeholder, node)
      })

      const text = (tmp.textContent || tmp.innerText || '').trim()

      return { text, mediaMap }
    },

    // Reinserts media back into the AI-transformed text by converting it
    // into paragraphs and swapping [[MEDIA_N]] tokens for the original HTML.
    buildFormattedOutput(aiText, mediaMap) {
      const lines = aiText.split('\n').map(line => {
        const escaped = document.createElement('div')
        escaped.textContent = line.trim()

        return escaped.innerHTML
      }).filter(line => line.length)
      const mediaRegex = /\[\[MEDIA_(\d+)\]\]/g
      const used = new Set()

      const parts = lines.map(line => {
        if (/^\[\[MEDIA_\d+\]\]$/.test(line)) {
          const idx = parseInt(line.match(/\d+/)[0], 10)
          used.add(idx)

          return mediaMap[idx] || ''
        }

        const replaced = line.replace(mediaRegex, (_, n) => {
          const idx = parseInt(n, 10)
          used.add(idx)

          return mediaMap[idx] || ''
        })

        return `<p>${replaced}</p>`
      })

      // Append any media the AI dropped, so nothing is lost.
      mediaMap.forEach((html, idx) => {
        if (!used.has(idx) && html) parts.push(html)
      })

      return parts.join('')
    },
    restoreOriginal() {
      this.$emit('input', this.originalText)
      this.$emit('update:originalText', null)
    },
    async runAction(action) {
      if (this.loading) return
      this.menuOpen = false
      this.aiMessage = ''
      this.aiFailed = false

      // Fallback: if the v-model value is missing media that is visible in the
      // wysiwyg editor (happens when images are injected via execCommand),
      // read the HTML directly from the contenteditable element.
      let sourceHtml = this.value || ''
      if (!/<(img|video|audio|iframe|picture|figure)\b/i.test(sourceHtml)) {
        const editable = this.$el.closest('form, .v-card, body')
          ? (this.$el.closest('form, .v-card, body').querySelector('[contenteditable]'))
          : document.querySelector('[contenteditable]')
        if (editable && /<(img|video|audio|iframe|picture|figure)\b/i.test(editable.innerHTML)) {
          sourceHtml = editable.innerHTML

          // Keep v-model in sync for downstream save operations.
          this.$emit('input', sourceHtml)
        }
      }

      const { text: plainText, mediaMap } = this.extractMedia(sourceHtml)
      if (!plainText) {
        return
      }
      if (plainText.length > 6000) {
        this.aiMessage = t('AI Text supports up to 6,000 characters per action.')
        this.aiFailed = true

        return
      }

      // Save original text before first AI transformation
      if (!this.originalText) {
        this.$emit('update:originalText', sourceHtml)
      }

      this.loading = true

      try {
        const payload = {
          action,
          text: plainText,
        }

        if (action === 'custom') {
          payload.customPrompt = this.customPrompt.trim()
          this.showCustomDialog = false
        }

        const response = { data: await runAi('text', payload) }

        if (response.data && response.data.result) {
          const formatted = this.buildFormattedOutput(response.data.result, mediaMap)
          this.$emit('input', formatted)
          this.aiMessage = t('AI text completed. 1 credit used.')
          if (action === 'custom') this.customPrompt = ''
        }
      } catch (err) {
        this.aiFailed = true
        this.aiMessage = aiError(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.ai-text-actions {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
  max-width: 100%;

  .v-alert { flex-basis: 100%; }
}

.ai-btn {
  text-transform: none;
  font-size: 12px;
  height: 28px !important;
}

.ai-menu-card {
  .v-list-item {
    cursor: pointer;

    &:hover {
      background-color: #f5f5f5;
    }
  }
}
</style>
